import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BRAND } from "@/lib/constants";
import { Zap, Menu, X, Download } from "lucide-react";

// "Performance" (the Stats section) was dropped to make room for "Pricing" — of the four,
// it was the least decision-relevant (a numbers readout, not something that helps someone decide
// to buy, unlike the calculator/FAQ/pricing itself).
const sectionLinks = [
  { id: "pricing", label: "Pricing" },
  { id: "free-scan", label: "Free Scan" },
  { id: "faq", label: "FAQ" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setMobileOpen(false);
    if (!onHome) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goDownload = () => goSection("download");

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 px-3 pt-3 ${scrolled || !onHome ? "" : ""}`}
      data-testid="site-nav"
    >
      <div
        className={`max-w-7xl mx-auto h-16 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${
          scrolled || !onHome ? "glass" : "bg-transparent border border-transparent"
        }`}
      >
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="relative">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <div className="absolute inset-0 blur-md bg-orange-500/40" />
          </div>
          <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {BRAND.name}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {sectionLinks.map((l) => (
            <button key={l.id} onClick={() => goSection(l.id)}
              className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors">
              {l.label}
            </button>
          ))}
          <Link to="/documentation" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors">
            Docs
          </Link>
          <Link to="/referral" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors">
            Referrals
          </Link>
          <Link to="/terms" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors">
            Terms and Policy
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goDownload}
            className="glass-hover hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-black font-black uppercase tracking-widest text-xs rounded-full hover:bg-orange-400 transition-colors"
            data-testid="nav-cta-download"
          >
            <Download className="w-3.5 h-3.5" /> Download Now
          </button>
          <button
            type="button"
            className="md:hidden p-2 text-zinc-300 hover:text-orange-500"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden max-w-7xl mx-auto mt-2 glass">
          <div className="px-4 py-4 flex flex-col gap-1">
            {sectionLinks.map((l) => (
              <button key={l.id} onClick={() => goSection(l.id)}
                className="text-left py-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300 hover:text-orange-500">
                {l.label}
              </button>
            ))}
            <Link to="/documentation" onClick={() => setMobileOpen(false)}
              className="py-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300 hover:text-orange-500">
              Docs
            </Link>
            <Link to="/referral" onClick={() => setMobileOpen(false)}
              className="py-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300 hover:text-orange-500">
              Referrals
            </Link>
            <Link to="/terms" onClick={() => setMobileOpen(false)}
              className="py-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300 hover:text-orange-500">
              Terms and Policy
            </Link>
            <button
              type="button"
              onClick={goDownload}
              className="mt-2 flex items-center justify-center gap-2 py-3 text-xs font-black uppercase tracking-[0.2em] bg-orange-500 text-black rounded-full"
            >
              <Download className="w-3.5 h-3.5" /> Download Now
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
};

